import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { JsonViewer } from "~/components/json-viewer";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatIDR, formatIDROptional, orDash } from "~/lib/format";
import { useTransaction, type TransactionDetail } from "~/lib/queries";
import { ProviderStatusPanel } from "./-provider-status";
import { OrderCallbacks, OrderTimeline } from "./-timeline";

export const Route = createFileRoute("/_authed/transactions/$orderId")({
  component: TransactionDetailPage,
});

/**
 * One order, everything Altura knows about it -- and, on request, what the
 * provider says too.
 *
 * The right-hand column is the M2 debugging surface: the unified timeline, this
 * order's callback deliveries, and a live DurianPay lookup. It was given a
 * column of its own before any of the three existed, which is why none of them
 * had to be wedged in.
 */
function TransactionDetailPage() {
  const { orderId } = Route.useParams();
  const { data, error, isPending, refetch } = useTransaction(orderId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return (
      <EmptyState
        title="No such order"
        body={
          <>
            Nothing in Altura has the order id{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{orderId}</code>.
            Soft-deleted orders do open here, so this id has never existed.
          </>
        }
      />
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Couldn't load this order"
        body={errorMessage(error)}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/transactions" />}>
          <ArrowLeftIcon className="size-3.5" />
          All transactions
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xl">{data.order_id}</span>
            <CopyButton value={data.order_id} label="order id" />
            {data.is_deleted ? <Badge variant="outline">deleted</Badge> : null}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.payment_status} />
            <span className="text-base font-medium text-foreground tabular-nums">
              {formatIDR(data.amount)}
            </span>
          </span>
        }
        actions={
          // An EXPIRED order is where a late payment lands (PRO-1345): one
          // click to whatever was recorded for it, open or resolved.
          data.payment_status === "EXPIRED" ? (
            <Button
              variant="outline"
              size="sm"
              render={
                <Link to="/payment-conflicts" search={{ state: "all", order_id: data.order_id }} />
              }
            >
              Payment conflicts
            </Button>
          ) : null
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0 space-y-5">
          <Overview detail={data} />
          <Payment detail={data} />
          <Items detail={data} />
          <Settlement detail={data} />
          <Raw detail={data} />
        </div>

        {/* The column PRO-1018 held open, now full: what happened to this
            order, what we told the merchant, and what the provider says. */}
        <aside className="space-y-4">
          <OrderTimeline orderId={data.order_id} />
          <OrderCallbacks orderId={data.order_id} />
          <ProviderStatusPanel orderId={data.order_id} />
        </aside>
      </div>
    </div>
  );
}

function Overview({ detail }: { detail: TransactionDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Order</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Storefront">
            {detail.storefront ? (
              <Link
                to="/storefronts/$storefrontId"
                params={{ storefrontId: detail.storefront_id }}
                className="text-primary hover:underline"
              >
                {detail.storefront.storefront_name}
              </Link>
            ) : (
              <span className="text-muted-foreground">{EM_DASH}</span>
            )}
          </DetailField>
          <DetailField label="Storefront ID" copy={detail.storefront_id} mono>
            {detail.storefront_id}
          </DetailField>
          {/* The reverse of the link on the bridge screen. Ops arrives from
              either direction depending on whether the merchant or the customer
              complained, and without this the trail only runs one way. Absent
              for a direct API order, which is most of them. */}
          {detail.bridge_order_id ? (
            <DetailField label="Shopify order">
              <Link
                to="/bridge/$shopifyOrderId"
                params={{ shopifyOrderId: detail.bridge_order_id }}
                className="font-mono text-[0.8125rem] text-primary hover:underline"
              >
                {detail.bridge_order_id}
              </Link>
            </DetailField>
          ) : null}
          <DetailField label="Amount">
            <span className="tabular-nums">{formatIDR(detail.amount)}</span>
          </DetailField>
          <DetailField label="Created">{formatDateTime(detail.created_at)}</DetailField>
          <DetailField label="Updated">{formatDateTime(detail.updated_at)}</DetailField>
          {/* Null for v1-created and pre-migration-010 rows, which is most of
              the table -- an em dash, never "Invalid Date". */}
          <DetailField label="Expires">{formatDateTime(detail.expires_at)}</DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

/**
 * The PSP identifiers, lifted out of additional_info and made prominent.
 *
 * These are the fields that correlate an Altura order with what DurianPay's
 * dashboard shows. Left in the raw blob at the bottom of the page they are
 * technically present and practically unusable, so they get first-class rows
 * and a copy button each.
 */
const PSP_FIELDS: { key: string; label: string }[] = [
  { key: "reference_no", label: "Reference no" },
  { key: "psp_payment_id", label: "PSP payment ID" },
  { key: "psp_order_id", label: "PSP order ID" },
  { key: "settlement_id", label: "Settlement ID" },
  { key: "payment_link_url", label: "Payment link" },
];

function Payment({ detail }: { detail: TransactionDetail }) {
  const psp = PSP_FIELDS.map((field) => ({
    ...field,
    value: readString(detail.additional_info, field.key),
  })).filter((field) => field.value !== undefined);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Payment</CardTitle>
        <CardDescription>
          Identifiers here are what match this order to the provider's records.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <FieldGrid>
          <DetailField label="Method">{detail.payment_method}</DetailField>
          <DetailField label="Provider">{detail.payment_provider}</DetailField>
          <DetailField label="Source">{orDash(detail.source)}</DetailField>
          {/* Not money: migration 008 gave every money column a minor-unit
              shadow and deliberately skipped this one. */}
          <DetailField label="Extra rate">{detail.extra_rate}</DetailField>
        </FieldGrid>

        {psp.length > 0 ? (
          <FieldGrid className="border-t pt-4">
            {psp.map((field) => (
              <DetailField key={field.key} label={field.label} copy={field.value} mono>
                {field.value}
              </DetailField>
            ))}
          </FieldGrid>
        ) : (
          <p className="border-t pt-4 text-sm text-muted-foreground">
            No provider identifiers recorded yet. They land when the payment is created and
            again when it settles.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Items({ detail }: { detail: TransactionDetail }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">Items</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {detail.transaction_items.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            No line items were recorded for this order.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Product</TableHead>
                <TableHead>Variant</TableHead>
                {/* "Qty", not "Amount". The column is called amount on
                    transaction_items and is a quantity; the money on this row is
                    unit_price and total. */}
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit price</TableHead>
                <TableHead className="pr-6 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.transaction_items.map((item, index) => (
                <TableRow key={`${item.product_name}-${index}`}>
                  <TableCell className="pl-6">{orDash(item.product_name)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {orDash(item.variant_name)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{item.amount}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatIDR(item.unit_price)}
                  </TableCell>
                  <TableCell className="pr-6 text-right tabular-nums">
                    {formatIDR(item.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Settlement({ detail }: { detail: TransactionDetail }) {
  const settlementId = readString(detail.additional_info, "settlement_id");
  const settledAt = readString(detail.additional_info, "settlement_date");
  const settled = detail.settled_amount != null || settlementId !== undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Settlement</CardTitle>
      </CardHeader>
      <CardContent>
        {settled ? (
          <FieldGrid>
            <DetailField label="Settled amount">
              <span className="tabular-nums">{formatIDROptional(detail.settled_amount)}</span>
            </DetailField>
            <DetailField label="Settlement fee">
              <span className="tabular-nums">{formatIDROptional(detail.settlement_fee)}</span>
            </DetailField>
            <DetailField label="Settled at">{formatDateTime(settledAt)}</DetailField>
            {/* Text, not a link: /settlements is still a placeholder, and
                pointing at it would be a dead end. It becomes a link when that
                screen lands. */}
            <DetailField label="Settlement ID" copy={settlementId} mono>
              {settlementId ?? EM_DASH}
            </DetailField>
          </FieldGrid>
        ) : (
          <p className="text-sm text-muted-foreground">This order has not settled.</p>
        )}
      </CardContent>
    </Card>
  );
}

function Raw({ detail }: { detail: TransactionDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Raw additional_info</CardTitle>
        <CardDescription>
          The whole jsonb column. Whatever the sections above miss is in here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <JsonViewer value={detail.additional_info} />
      </CardContent>
    </Card>
  );
}

/**
 * Reads one key out of a jsonb blob as a display string.
 *
 * Tolerant on purpose: the provider's payload shapes are inconsistent, and a
 * number where a string was expected should render, not blank the section. An
 * object or an array is left to the raw viewer below.
 */
function readString(info: Record<string, unknown> | null, key: string): string | undefined {
  const value = info?.[key];
  if (typeof value === "string") return value.trim() === "" ? undefined : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="space-y-5">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: React.ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/transactions" />}>
            Back to transactions
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
